/**
 * System → project library import with `systemExampleId` provenance (drives
 * the Imported badge and re-import no-ops). The browser prepares the selected
 * system record, then `BlockLibraryService.create` delegates the write to
 * Builder's receipt-bound transaction, including the v2 validator, exact byte
 * guard, project cap, and authoritative readback.
 */

import type { BlockLibraryExampleDoc, BlockLibraryExampleInput } from '@modules/shared-types';
import type { BlockLibraryService } from '@modules/firebase-core/services/BlockLibraryService';

export interface ImportSystemExamplesResult {
  imported: string[];
  skippedAlreadyImported: string[];
}

export async function importSystemExamples(
  systemDocs: BlockLibraryExampleDoc[],
  projectExamples: Pick<BlockLibraryExampleDoc, 'systemExampleId'>[],
  service: Pick<BlockLibraryService, 'create'>,
): Promise<ImportSystemExamplesResult> {
  const alreadyImported = new Set(
    projectExamples
      .map((example) => example.systemExampleId)
      .filter((id): id is string => Boolean(id)),
  );

  const result: ImportSystemExamplesResult = { imported: [], skippedAlreadyImported: [] };
  for (const doc of systemDocs) {
    if (alreadyImported.has(doc.id)) {
      result.skippedAlreadyImported.push(doc.id);
      continue;
    }
    const {
      id, createdBy, createdAt, updatedBy: _updatedBy, updatedAt: _updatedAt,
      ...input
    } = doc as BlockLibraryExampleDoc & Record<string, unknown>;
    void createdBy;
    void createdAt;
    await service.create({ ...(input as BlockLibraryExampleInput), systemExampleId: id });
    result.imported.push(id);
  }
  return result;
}
